<script setup lang="ts">
import { formatTimeAgo } from '@vueuse/core'
import type { A2uiClientAction } from '@a2ui/web_core/v0_9'
import type { A2uiSurfaceInput } from '~/utils/a2ui'
import type { AgentChatMessageMetadata } from '~/composables/useUserAgentChat'

type Message = AgentChat['messages'][number]
type Part = Message['parts'][number]

type PartView =
  | { kind: 'text'; text: string }
  | { kind: 'reasoning'; text: string; streaming: boolean }
  | { kind: 'surface'; id: string; surface: A2uiSurfaceInput }
  | { kind: 'tool'; label: string; pending: boolean }

const { chat, models, model, open, history, send, regenerate, clear, restore } =
  useUserAgentChat()

const historyItems = computed(() =>
  history.value.map(({ id, title, updatedAt }) => ({
    label: title,
    description: formatTimeAgo(new Date(updatedAt)),
    onSelect: () => void restore(id),
  })),
)
const { connectionState } = useUserAgent()
const connected = computed(() => connectionState.value === 'connected')

const input = ref('')
const onSubmit = () => {
  const text = input.value.trim()
  if (!text || !connected.value) return
  void send(text)
  input.value = ''
}

const TOOL_LABELS: Record<string, string> = {
  getMyData: 'Read your todos, notifications and favorites',
  createTodo: 'Created a todo',
  updateTodo: 'Updated a todo',
  deleteTodo: 'Deleted a todo',
  markNotificationsRead: 'Marked notifications as read',
  updateSettings: 'Updated your settings',
  renderUI: 'Building a view',
  searchPolicies: 'Searched policies',
  getPolicy: 'Read a policy',
  searchSourceDocuments: 'Searched source documents',
  searchNucleusDocuments: 'Searched Policy Studio documents',
  getNucleusDocument: 'Read a Policy Studio document',
}

const viewOf = (part: Part): PartView | undefined => {
  if (part.type === 'text') return { kind: 'text', text: part.text }
  if (part.type === 'reasoning') {
    return part.text
      ? {
          kind: 'reasoning',
          text: part.text,
          streaming: part.state === 'streaming',
        }
      : undefined
  }
  if (!('toolCallId' in part)) return
  const name =
    part.type === 'dynamic-tool'
      ? part.toolName
      : part.type.slice('tool-'.length)
  const pending =
    part.state !== 'output-available' && part.state !== 'output-error'
  /* The agent rejects malformed surfaces and retries; only accepted ones render. */
  const rejected =
    (part.output as { rendered?: boolean } | undefined)?.rendered === false
  if (name === 'renderUI' && part.state !== 'input-streaming') {
    return rejected
      ? undefined
      : {
          kind: 'surface',
          id: part.toolCallId,
          surface: part.input as A2uiSurfaceInput,
        }
  }
  return { kind: 'tool', label: TOOL_LABELS[name] ?? name, pending }
}

const actionOf = (message: Message) =>
  (message.metadata as AgentChatMessageMetadata | undefined)?.a2uiAction

const onAction = ({ name, context }: A2uiClientAction) => {
  if (!connected.value || chat.status !== 'ready') return
  void send(`[UI action] ${name}\n${JSON.stringify(context)}`, {
    a2uiAction: name,
  })
}
</script>

<template>
  <section
    v-show="open"
    id="agent-chat"
    class="border-default bg-default fixed bottom-10 right-3 z-50 flex h-[min(40rem,calc(100dvh-3.5rem))] w-[min(26rem,calc(100vw-1.5rem))] flex-col overflow-hidden rounded-lg border shadow-xl"
    aria-label="Agent chat"
    @keydown.esc="open = false"
  >
    <header
      class="border-default flex h-10 shrink-0 items-center gap-1 border-b px-3"
    >
      <UIcon name="i-ph-sparkle" class="text-primary size-4" />
      <h2 class="text-highlighted flex-1 text-sm font-medium">Agent</h2>
      <UDropdownMenu
        :content="{ align: 'end' }"
        :disabled="!history.length"
        :items="historyItems"
        :ui="{ content: 'w-72 max-h-80' }"
      >
        <UTooltip text="Chat history">
          <UButton
            :disabled="!history.length"
            aria-label="Chat history"
            color="neutral"
            icon="i-ph-clock-counter-clockwise"
            size="xs"
            variant="ghost"
          />
        </UTooltip>
      </UDropdownMenu>
      <UTooltip text="New chat">
        <UButton
          :disabled="!chat.messages.length"
          aria-label="New chat"
          color="neutral"
          icon="i-ph-note-pencil"
          size="xs"
          variant="ghost"
          @click="clear"
        />
      </UTooltip>
      <UButton
        aria-label="Close agent chat"
        color="neutral"
        icon="i-ph-x"
        size="xs"
        variant="ghost"
        @click="open = false"
      />
    </header>

    <div class="min-h-0 flex-1 overflow-y-auto px-3">
      <div
        v-if="!chat.messages.length"
        class="text-muted flex h-full flex-col items-center justify-center gap-1 text-center text-sm"
      >
        <UIcon name="i-ph-sparkle" class="size-6" />
        <p>Ask about your todos, notifications or favorites.</p>
      </div>
      <UChatMessages
        v-else
        class="py-3"
        :assistant="{ side: 'left', variant: 'naked' }"
        :messages="chat.messages"
        :spacing-offset="0"
        :status="chat.status"
        :user="{ side: 'right', variant: 'soft' }"
        compact
      >
        <template #indicator>
          <UChatShimmer class="text-sm" text="Thinking…" />
        </template>
        <template #content="{ message }">
          <UBadge
            v-if="actionOf(message)"
            :label="actionOf(message)"
            icon="i-ph-cursor-click"
            variant="subtle"
          />
          <template
            v-for="(view, index) in message.parts.map(viewOf)"
            v-else
            :key="`${message.id}-${index}`"
          >
            <template v-if="view?.kind === 'text'">
              <CollapsibleMarkdown
                v-if="message.role === 'assistant'"
                class="text-sm"
                :lines="0"
                :value="view.text"
              />
              <p v-else class="whitespace-pre-wrap text-sm">{{ view.text }}</p>
            </template>
            <UChatReasoning
              v-else-if="view?.kind === 'reasoning'"
              :streaming="view.streaming"
              :text="view.text"
            />
            <A2uiSurface
              v-else-if="view?.kind === 'surface'"
              class="my-2"
              :surface="view.surface"
              :surface-id="view.id"
              @action="onAction"
            />
            <UChatTool
              v-else-if="view?.kind === 'tool'"
              :loading="view.pending"
              :streaming="view.pending"
              :text="view.label"
            />
          </template>
        </template>
      </UChatMessages>
    </div>

    <UAlert
      v-if="chat.error"
      class="mx-3 mb-2 w-auto shrink-0"
      :description="chat.error.message"
      :ui="{ description: 'wrap-anywhere max-h-32 overflow-y-auto' }"
      close
      color="error"
      icon="i-ph-warning-circle"
      title="The agent couldn't respond"
      variant="subtle"
      @update:open="chat.clearError()"
    />

    <UChatPrompt
      v-model="input"
      class="border-default shrink-0 rounded-none border-t"
      :error="chat.error"
      :maxrows="8"
      :rows="1"
      :ui="{ base: 'px-0 text-sm' }"
      autofocus
      placeholder="Ask the agent…"
      variant="naked"
      @submit="onSubmit"
    >
      <template #footer>
        <USelectMenu
          v-model="model"
          class="min-w-0"
          :content="{ align: 'start' }"
          :items="models"
          :search-input="{ placeholder: 'Search models…' }"
          :ui="{ content: 'min-w-72' }"
          aria-label="Model"
          color="neutral"
          description-key="description"
          label-key="label"
          size="xs"
          value-key="id"
          variant="ghost"
        />
        <UChatPromptSubmit
          :disabled="!connected && chat.status === 'ready'"
          :status="chat.status"
          size="xs"
          @reload="regenerate()"
          @stop="chat.stop()"
        />
      </template>
    </UChatPrompt>
  </section>
</template>
